import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Divider,
  Modal,
  message,
} from '@surecompanies/core_components'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import { uploadToS3 } from 'enrollment-platform/pages/PlanManagementV2/utils/uploadToS3'
import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  dataTypeLabel,
  type SurecoCsvDataType,
} from '../constants'
import {
  getActiveHealthPlanImportJob,
  getHealthPlanImportJob,
  presignHealthPlanUpload,
  startHealthPlanImport,
  type HealthPlanImportResultDto,
} from '../api/health-plan-api'
import { useCsvDataTypeDetection } from '../hooks/useCsvDataTypeDetection'
import { CsvDetectionPhaseContent } from './CsvDetectionPhaseContent'
import CsvFileDropzone from './CsvFileDropzone'

// What happens once detection confidently identifies a data type — the part
// that's specific to Health Plan (a confirm screen, then the real import job)
// rather than shared with Compare Data (which proceeds immediately). 'none'
// means the shared idle/detecting/columnErrors/noMatch phases (from
// useCsvDataTypeDetection) are in charge of what's rendered.
type ExtraPhase = 'none' | 'confirm' | 'uploading' | 'processing' | 'result'

const JOB_POLL_INTERVAL_MS = 2_000
const MAX_POLL_FAILURES = 3
const TERMINAL_JOB_STATES = new Set([
  'completed',
  'failed',
  'cancelled',
  'expired',
])

interface Props {
  open: boolean
  onClose: () => void
  onUploaded: () => void
}

export default function UploadHealthPlanCsvModal({
  open,
  onClose,
  onUploaded,
}: Props) {
  const [extraPhase, setExtraPhase] = useState<ExtraPhase>('none')
  const [uploadPercent, setUploadPercent] = useState(0)
  const [result, setResult] = useState<HealthPlanImportResultDto | null>(null)
  // Filename for a job resumed on reopen — there's no File object for it, unlike the hook's file.
  const [resumedFilename, setResumedFilename] = useState<string | null>(null)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Bumped by handleClose and unmount — a stale session's in-flight request only acts if its own value still matches.
  const sessionRef = useRef(0)
  const pollFailureCountRef = useRef(0)

  // Recursive setTimeout, not setInterval, so a slow check can't overlap the next one.
  const pollJob = useCallback(
    (jobId: string) => {
      const session = sessionRef.current
      pollTimeoutRef.current = setTimeout(() => {
        void (async () => {
          if (session !== sessionRef.current) return
          try {
            const job = await getHealthPlanImportJob(jobId)
            if (session !== sessionRef.current) return
            pollFailureCountRef.current = 0
            if (job.state === 'completed' && job.output) {
              setResult(job.output)
              setExtraPhase('result')
              onUploaded()
              return
            }
            if (TERMINAL_JOB_STATES.has(job.state)) {
              message.error('Import failed. Please try again.')
              setExtraPhase('none')
              setResumedFilename(null)
              reset()
              return
            }
            pollJob(jobId)
          } catch (err) {
            if (session !== sessionRef.current) return
            // eslint-disable-next-line no-console
            console.error('Failed to check import job status', err)
            pollFailureCountRef.current += 1
            if (pollFailureCountRef.current < MAX_POLL_FAILURES) {
              pollJob(jobId)
              return
            }
            message.error('Could not check import status. Please try again.')
            setExtraPhase('none')
            setResumedFilename(null)
            reset()
          }
        })()
      }, JOB_POLL_INTERVAL_MS)
    },
    // `reset` omitted deliberately — it's declared after this call, referencing it here would TDZ-error.
    [onUploaded]
  )

  // Resume an in-progress job for this type, else move to the confirm screen.
  const handleConfidentMatch = useCallback(
    (dataType: SurecoCsvDataType) => {
      const session = sessionRef.current
      void (async () => {
        try {
          const activeJob = await getActiveHealthPlanImportJob(dataType)
          if (session !== sessionRef.current) return
          if (activeJob.jobId) {
            setResumedFilename(activeJob.originalFilename)
            setExtraPhase('processing')
            pollFailureCountRef.current = 0
            pollJob(activeJob.jobId)
            return
          }
          setExtraPhase('confirm')
        } catch (err) {
          if (session !== sessionRef.current) return
          // eslint-disable-next-line no-console
          console.error('Failed to check for an in-progress import', err)
          message.error('Could not check import status. Please try again.')
          reset()
        }
      })()
    },
    [pollJob]
  )

  const {
    phase: detectPhase,
    file,
    setFile,
    detectedDataType,
    columnErrors,
    detect,
    reset,
  } = useCsvDataTypeDetection(handleConfidentMatch)

  useEffect(() => {
    return () => {
      sessionRef.current += 1
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  const resetUploadState = useCallback(() => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    pollFailureCountRef.current = 0
    setExtraPhase('none')
    setUploadPercent(0)
    setResult(null)
    setResumedFilename(null)
    reset()
  }, [reset])

  const handleConfirmUpload = useCallback(async () => {
    if (!file || !detectedDataType) return
    const session = sessionRef.current
    setExtraPhase('uploading')
    setUploadPercent(0)
    try {
      const { folder, uploadUrl } = await presignHealthPlanUpload(
        file.name,
        file.size
      )
      const uploadResult = await uploadToS3({
        presignedUrl: uploadUrl,
        file,
        contentType: 'text/csv',
        onProgress: setUploadPercent,
      })
      if (!uploadResult.success) {
        throw uploadResult.error ?? new Error('S3 upload failed')
      }

      const { jobId } = await startHealthPlanImport({
        folder,
        originalFilename: file.name,
        dataType: detectedDataType,
      })
      if (session !== sessionRef.current) return
      setExtraPhase('processing')
      pollFailureCountRef.current = 0
      pollJob(jobId)
    } catch (err) {
      if (session !== sessionRef.current) return
      // eslint-disable-next-line no-console
      console.error('Failed to import health plan csv', err)
      message.error('Import failed. Please try again.')
      setExtraPhase('none')
      setResumedFilename(null)
      reset()
    }
  }, [file, detectedDataType, pollJob, reset])

  const handleClose = useCallback(() => {
    if (extraPhase === 'uploading' || extraPhase === 'processing') {
      return
    }
    sessionRef.current += 1
    resetUploadState()
    onClose()
  }, [extraPhase, resetUploadState, onClose])

  const isResult = extraPhase === 'result'
  const isFilePickerPhase = extraPhase === 'none' && detectPhase === 'idle'

  return (
    <Modal
      open={open}
      title="Upload Health Plan CSV"
      onCancel={handleClose}
      maskClosable={false}
      footer={null}
      width={640}
      centered
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Divider style={{ margin: 0 }} />
        {isResult && result && (
          <Alert
            type={result.success ? 'success' : 'error'}
            showIcon
            message={
              result.errorMessage
                ? `Import failed unexpectedly: ${result.errorMessage}`
                : result.success
                  ? `${result.inserted} inserted, ${result.updated} updated of ${result.totalRows} rows.`
                  : `Nothing was applied — ${result.rowErrors} of ${result.totalRows} row(s) failed validation.`
            }
            description={
              result.rowErrors > 0
                ? 'Download the row error file from Upload Logs for details.'
                : undefined
            }
          />
        )}

        {extraPhase === 'none' && detectPhase !== 'idle' && (
          <CsvDetectionPhaseContent
            phase={detectPhase}
            detectedDataType={detectedDataType}
            columnErrors={columnErrors}
          />
        )}

        {extraPhase === 'confirm' && detectedDataType && file && (
          <>
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                Confirm file upload
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                Review the details below, then confirm to upload.
              </div>
            </div>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '12px 16px',
                fontSize: 13,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <span>
                <strong>Data type:</strong> {dataTypeLabel(detectedDataType)}
              </span>
              <span>
                <strong>File name:</strong> {file.name}
              </span>
            </div>
          </>
        )}

        {isFilePickerPhase && (
          <CsvFileDropzone
            file={file}
            onFileSelect={setFile}
            onRemoveFile={() => setFile(null)}
            maxFileSizeBytes={MAX_UPLOAD_FILE_SIZE_BYTES}
          />
        )}

        {(extraPhase === 'uploading' || extraPhase === 'processing') && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
            }}
          >
            <FileTextOutlined style={{ fontSize: 24, color: '#6b7280' }} />
            <span style={{ flex: 1, fontWeight: 500 }}>
              {resumedFilename ?? file?.name}
              {extraPhase === 'uploading' && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: '#6b7280',
                    marginTop: 4,
                  }}
                >
                  Uploading… {uploadPercent}%
                </div>
              )}
              {extraPhase === 'processing' && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: '#6b7280',
                    marginTop: 4,
                  }}
                >
                  <LoadingOutlined style={{ marginRight: 6 }} />
                  Processing your upload…
                </div>
              )}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {isResult && (
            <Button type="primary" onClick={resetUploadState}>
              Upload another
            </Button>
          )}

          {extraPhase === 'none' && detectPhase === 'noMatch' && (
            <Button type="primary" onClick={reset}>
              Try again
            </Button>
          )}

          {extraPhase === 'none' && detectPhase === 'columnErrors' && (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" onClick={reset}>
                Continue
              </Button>
            </>
          )}

          {extraPhase === 'confirm' && (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" onClick={() => void handleConfirmUpload()}>
                Upload
              </Button>
            </>
          )}

          {extraPhase === 'none' && detectPhase === 'detecting' && (
            <Button onClick={handleClose}>Cancel</Button>
          )}

          {isFilePickerPhase && (
            <Button
              type="primary"
              onClick={() => void detect()}
              disabled={!file}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
